import React from 'react';
import Header from './Header';
import { motion } from 'framer-motion';
import { VARIANTS } from '../constants/variants';
import Footer from './Footer';
import ApolloError from '@shared/components/ApolloError';
import Section from './Section';
import { useNotificationPagination } from '../hooks/useNotificationPagination';
import useNotificationActions from '../hooks/useNotificationActions';
import { useNotificationCenter } from 'react-toastify/addons/use-notification-center';
import { useIsOpenedStore } from '../NotificationCenterStore';
import { useShallow } from 'zustand/shallow';

/**
 * @typedef {object} ToastNotificationData
 * @property {boolean} [exclude]
 */

/** @type {typeof useNotificationCenter<ToastNotificationData>} */
const useToastNotificationCenter = useNotificationCenter;

const Container = () => {
  const isOpen = useIsOpenedStore(useShallow(state => state.isOpened));

  const {
    notifications,
    markAsRead,
    markAllAsRead,
    add: addNotification,
    clear
  } = useToastNotificationCenter({
    filter: item => !item.data?.exclude
  });

  const {
    showUnreadOnly,
    toggleShowUnread,
    loading,
    filteredNotifications,
    fetchMoreData,
    hasMore
  } = useNotificationPagination(notifications, addNotification, clear);

  const { notificationActionError, handleMarkAsReadClick, handleMarkAllAsReadClick } =
    useNotificationActions(markAsRead, markAllAsRead);

  if (notificationActionError) {
    return <ApolloError />;
  }

  return (
    <motion.aside
      animate={isOpen ? 'open' : 'closed'}
      variants={VARIANTS.CONTAINER_VARIANT}
      initial={false}
      className='notification-container'
      style={{ pointerEvents: isOpen ? 'auto' : 'none' }}>
      <Header showUnreadOnly={showUnreadOnly} toggleShowUnreadOnly={toggleShowUnread} />
      <Section
        notifications={filteredNotifications}
        handleMarkAsReadClick={handleMarkAsReadClick}
        loading={loading}
        fetchMoreData={fetchMoreData}
        hasMore={hasMore}
        showUnreadOnly={showUnreadOnly}
      />
      <Footer handleMarkAllAsReadClick={handleMarkAllAsReadClick} disableMarkAllAsRead={loading} />
    </motion.aside>
  );
};

export default Container;
